import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {Divider, Grid, TextInput} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import AntibodyChainLegend from 'antibodies/antibody-map/AntibodyChainLegend';
import {VeritasHelpGuideModal} from 'antibodies/format/help-modal/VeritasHelpGuideModal';
import {useVeritasFormatPreviewQuery} from 'antibodies/format/useVeritasFormatPreviewQuery';
import Antibody from 'antibodies/visualization/Antibody';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {FONT_PROXIMA_13, FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import runModal from 'ui-library/runModal';
import {CenteredSpinner} from 'ui-library/Spinner';

export interface AntibodyFormatCreationPaneProps {
  onRequestClose: () => void;
  onCreate?: () => void;
}

const EXAMPLE_FORMATS = ['Fab-Fc', 'IgG-scFab', '[Fd:LC*scFv]-CH3'];

export default function AntibodyFormatCreationPane({
  onRequestClose,
  onCreate,
}: AntibodyFormatCreationPaneProps): JSX.Element {
  // TODO(BNCH-136568): Use this name to create formats (default to veritas name if not provided).
  const [formatName, setFormatName] = useState('');
  const [veritasName, setVeritasName] = useState('');
  const [previewName, setPreviewName] = useState<string | null>(null);

  const {status, result} = useVeritasFormatPreviewQuery(previewName);

  const handlePreview = (): void => {
    setPreviewName(veritasName);
  };

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && veritasName.trim().length > 0) {
      handlePreview();
    }
  };

  const handleExampleFormatClick = (notation: string): void => {
    setVeritasName(notation);
    setPreviewName(notation);
  };

  const handleClear = (): void => {
    setVeritasName('');
    setPreviewName(null);
  };

  const handleOpenHelpGuide = (): void => {
    runModal(VeritasHelpGuideModal, {});
  };

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.header)}>
        <div className={css(styles.headerTitle)}>Create new antibody format</div>
        <div className={css(styles.headerButtons)}>
          <Button
            onClick={onRequestClose}
            text="Cancel"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
          />
          <Button
            onClick={onCreate}
            text="Create"
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.SMALL}
            disabled={true} // TODO(BNCH-136568): Allow creation of formats.
          />
        </div>
      </div>
      <Divider />
      <Grid gutter="md">
        <Grid.Col span={6} p="xl">
          <div className={css(styles.veritasHeader)}>
            <span>Create antibody format using VERITAS</span>
            <Button
              onClick={handleOpenHelpGuide}
              icon="more-info"
              text="How to use VERITAS"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
            />
          </div>
          <FormGroup>
            <FieldLabel label="Display name" htmlFor="display-name" />
            <TextInput
              id="display-name"
              onChange={(e) => {
                setFormatName(e.target.value);
              }}
              placeholder="Enter display name"
              value={formatName}
              className={css(styles.displayNameInput)}
            />
            <Divider />
            <div className={css(styles.exampleFormatsSection)}>
              <span className={css(styles.exampleLabel)}>Examples:</span>
              {EXAMPLE_FORMATS.map((format) => (
                <Button
                  key={format}
                  buttonStyle={ButtonStyle.LINK}
                  text={format}
                  onClick={() => {
                    handleExampleFormatClick(format);
                  }}
                />
              ))}
            </div>
            <TextInput
              onChange={(e) => {
                setVeritasName(e.target.value);
              }}
              onKeyDown={handleKeyPress}
              placeholder="Enter VERITAS notation (e.g. IgG-[scFv*])"
              value={veritasName}
            />
            <div className={css(styles.previewButtons)}>
              <Button
                onClick={handleClear}
                icon="close"
                text="Clear"
                buttonStyle={ButtonStyle.SECONDARY_WHITE}
                size={ButtonSize.SMALL}
                disabled={veritasName.trim().length === 0}
              />
              <Button
                onClick={handlePreview}
                icon="eye"
                text="Preview"
                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                size={ButtonSize.SMALL}
                disabled={veritasName.trim().length === 0}
              />
            </div>
          </FormGroup>
        </Grid.Col>
        <Grid.Col span={6} p="xl">
          <div className={css(styles.previewPanel)}>
            {previewName == null ? (
              <div className={css(styles.previewPlaceholder)}>
                Enter VERITAS notation to preview antibody format
              </div>
            ) : (
              <>
                {status === QueryResultType.PENDING && <CenteredSpinner />}
                {status === QueryResultType.ERROR && (
                  <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to preview format." />
                )}
                {status === QueryResultType.SUCCESS &&
                  result != null &&
                  result.__typename === 'VeritasFormatPreviewErrorNode' && (
                    <InlineBanner
                      bannerType={InlineBannerType.ERROR}
                      text={`Unable to preview format: ${result.message}`}
                    />
                  )}
                {status === QueryResultType.SUCCESS &&
                  result != null &&
                  result.__typename === 'AntibodyFormatNode' && (
                    <div
                      {...getTestProps({
                        component: 'AntibodyFormatCreationPane',
                        element: 'preview-container',
                      })}
                    >
                      <AntibodyChainLegend antibodyFormat={result} />
                      <Antibody format={result} showLabels={true} showSpecificity={true} />
                    </div>
                  )}
              </>
            )}
          </div>
        </Grid.Col>
      </Grid>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    height: '56px',
  },
  headerTitle: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  headerButtons: {
    display: 'flex',
    gap: '12px',
  },
  veritasHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  displayNameInput: {
    marginBottom: '16px',
  },
  exampleFormatsSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    margin: '16px 0',
  },
  exampleLabel: {
    ...FONT_PROXIMA_13,
  },
  previewButtons: {
    display: 'flex',
    gap: '8px',
    margin: '16px 0',
    justifyContent: 'flex-end',
  },
  previewPanel: {
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
    padding: '8px',
    minHeight: '304px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  previewPlaceholder: {
    ...FONT_PROXIMA_13,
    color: Color.GRAY_500,
    textAlign: 'center',
  },
});
